import { NextPage, NextPageContext } from 'next';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { Fragment, useMemo } from 'react';

import { Container } from '@/components/core/Grid';
import { FeaturedArticle } from '@/components/News/FeaturedArticle';
import { Layout } from '@/components/ui/shared/layouts/Layout/Layout';
import { ArticlesList } from '@/containers/News/ArticlesList';
import { Header } from '@/containers/News/Header';
import { AccessFrom } from '@/enums/AccessFrom';
import { useStores } from '@/hooks/useStores';
import { IPageError } from '@/interfaces/pageError';
import Error from '@/pages/_error';
import { NewsArticle } from '@/resources/NewsArticle';
import { NewsArticleTopic } from '@/resources/NewsArticleTopic';
import { AppCollection as AppStore } from '@/stores/AppStore';
import { gIPDecorator } from '@/utils/gIPDecorator';

interface INextPageProps {
  error?: IPageError;
  articleIds?: Array<string>;
  articleTopicsIds?: Array<string>;
  articlesNext?: string;
}

const NewsPage: NextPage<INextPageProps> = ({ error, articleIds, articleTopicsIds, articlesNext }) => {
  const { store } = useStores();
  const router = useRouter();

  const articles = store.findByIds(NewsArticle, articleIds);
  const articleTopics = store.findByIds(NewsArticleTopic, articleTopicsIds);

  const activeArticleTopic = useMemo(() => {
    return articleTopics.find((topic) => topic.id === router.query.articleTopicId)?.name;
  }, [articleTopics, router.query.articleTopicId]);

  const filteredArticleTopics = useMemo(() => {
    if (activeArticleTopic) {
      return articleTopics.filter((topic) => topic.id !== router.query.articleTopicId);
    }

    return articleTopics;
  }, [activeArticleTopic, articleTopics, router.query.articleTopicId]);

  if (error) {
    return <Error statusCode={error.statusCode} />;
  }

  return (
    <Fragment>
      <NextSeo title="Science News" />
      <Layout navigationVariant="inverted">
        <Container>
          <Header
            articleTopics={filteredArticleTopics}
            activeArticleTopic={router.query.articleTopicId && activeArticleTopic}
          />
          {articles.length > 0 && <FeaturedArticle article={articles[0]} />}
          <ArticlesList articles={articles.length > 1 && articles.slice(1)} initialNextArticlesLink={articlesNext} />
        </Container>
      </Layout>
    </Fragment>
  );
};

interface IContext extends NextPageContext {
  store: AppStore;
  query: {
    articleTopicId: string;
  };
}

NewsPage.getInitialProps = gIPDecorator(async (ctx: IContext) => {
  try {
    const articleTopicsRequest = ctx.store.fetchAll(NewsArticleTopic);

    const articleRequest = await ctx.store.fetchAll(NewsArticle, {
      filter: {
        ...(ctx.query.articleTopicId && { news_article_topic_id: ctx.query.articleTopicId }),
        scope: 'published',
      },
      params: [
        {
          key: 'page[size]',
          value: '10',
        },
        {
          key: 'page[number]',
          value: '1',
        },
      ],
    });

    const [articleTopicsResponse, articlesResponse] = await Promise.all([articleTopicsRequest, articleRequest]);

    const articleTopics = articleTopicsResponse.data as Array<NewsArticleTopic>;
    const articles = articlesResponse.data as Array<NewsArticle>;
    const articlesNext = articlesResponse.links.next as string;

    return {
      articleIds: articles.map((article) => article.id),
      articleTopicsIds: articleTopics.map((topics) => topics.id),
      articlesNext,
    };
  } catch (error) {
    return {
      error: {
        statusCode: error.status,
      },
    };
  }
});

export default NewsPage;
